import { useCallback, useMemo, useRef } from "react"
import { Box, useTheme, Fab, Slide } from "@mui/material"
import { useNavigate } from "react-router-dom"

import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"
import { useFeatureDetail } from "hooks/feature/useFeatureDetail"
import { usePlaceDetailMap } from "hooks/place/usePlaceDetailMap"
import { useMobileUrl } from "hooks/useMobileUrl"
import { useGeoLocation } from "providers/geolocation"

import { getIcon } from "utils/icon"
import {
  CorrelationSelectorSplide,
  NextopiaPlaceDetailCard,
  PlaceDetailCard,
} from "extensions/one-siam/components/domains/features"

import { Head } from "components/Head"
import { BackIcon } from "components/icons"

import { createOpeningHours } from "utils/date"
import {
  NEXTOPIA_CATEGORY_TYPE,
  NEXTOPIA_POST_TYPE,
} from "extensions/one-siam/constants"
import { get, orderBy, take } from "lodash"
import { usePost } from "hooks/post/usePost"
import { getPostDetail } from "utils/feature"
import { useVenue } from "providers/venue"
import { isValidURL } from "utils/url"
import { useOccupantFloors } from "hooks/occupant/useOccupantFloors"
import { AppWeatherWidget } from "extensions/one-siam/components/domains/weather"
import { useNextopiaPlaceDetailMap } from "extensions/one-siam/hooks"

export const PlaceDetailKioskVPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const dialogRef = useRef(null)
  const { deviceType } = useVenue()
  const { goBack } = useAppUI()
  const { dataLoaded, ...data } = useFeatureDetail()

  const {
    parentCategoryRefs,
    feature,
    id,
    name,
    isNavigatable = true,
    correlation,
  } = data

  const { deviceLocation } = useGeoLocation()

  const canNavigate = !!deviceLocation && isNavigatable

  const { mobileUrl } = useMobileUrl()

  // NOTE: This logic is specific to OneSiam for displaying Nextopia place details.
  const isNextopiaSection =
    feature?.feature_type === "section" &&
    parentCategoryRefs.includes(NEXTOPIA_CATEGORY_TYPE)

  const hasSectionFloors =
    isNextopiaSection &&
    Array.isArray(correlation?.floorOptions) &&
    correlation?.floorOptions.length > 1

  const { postData } = usePost(NEXTOPIA_POST_TYPE, {
    enabled: isNextopiaSection,
  })

  const transformedPosts = useMemo(
    () => take(postData?.map(getPostDetail), 5),
    [postData]
  )

  const {
    floorOptions,
    currentFloor,
    hasFloors: hasOccupantFloors,
  } = useOccupantFloors(feature)

  const transformedData = useMemo(() => {
    const { open = "", close = "" } = createOpeningHours(data?.hours)
    return {
      ...data,
      open,
      close,
    }
  }, [data])

  const sortedFloorOptions = useMemo(
    () => orderBy(correlation?.floorOptions || [], ["ordinal"], ["desc"]),
    [correlation]
  )

  usePlaceDetailMap(feature, {
    enabled: !!feature && !isNextopiaSection,
    offset: { bottom: 315 },
  })

  useNextopiaPlaceDetailMap(feature, {
    offset: {
      bottom: 600,
    },
    zoomOffset: 0.1,
    enabled: !!feature && isNextopiaSection,
  })

  const theme = useTheme()
  const { styleOverrides = {} } = theme.components[`AppDetailPage`] || {}
  const {
    root,
    wrapper,
    kioskBackButton,
    correlationSelector,
    correlationOccupantSelector,
    weatherWidget,
  } = styleOverrides

  const backIcon = useMemo(
    () => getIcon(theme?.assets?.backIcon) || <BackIcon />,
    [theme]
  )

  const onNavigate = useCallback(() => {
    if (!canNavigate) return
    const locationId = feature?.properties?.feature?.id || id
    navigate(`/maps/dir/unknown/${locationId}`)
  }, [navigate, feature, canNavigate, id])

  const onViewAllShops = useCallback(() => {
    const parentCategories = get(
      feature,
      "properties.local_parent_categories",
      []
    )
    const venueId = get(feature, "properties.venue_id", [])
    const matchedCategory = parentCategories.find(
      (category) =>
        get(category, "properties.reference") === NEXTOPIA_CATEGORY_TYPE
    )
    const path = matchedCategory?.id
      ? `/directory?parentCategoryId=${matchedCategory.id}&subCategoryId=ALL&ordinal=ALL&venue=${venueId}`
      : null

    if (path) navigate(path, { replace: true })
  }, [feature, navigate])

  const onClose = useCallback(() => {
    goBack()
  }, [goBack])

  const handleHighlightClick = useCallback(
    (data) => {
      const location = data?.locationLinkInfo
      const path = location?.url
      const isExternalLink = location?.type === "custom" && isValidURL(path)
      const isNewTab = location?.is_new_tab
      const canOpenExternalLink = isExternalLink && deviceType === "mobile"

      if (!path) return

      if (canOpenExternalLink) {
        window.open(path, isNewTab ? "_blank" : "_self")
        return
      }

      navigate(path)
    },
    [deviceType, navigate]
  )

  const handleCorrelationChange = useCallback(
    (id) => {
      navigate(`/maps/place/${id}`, { replace: true })
    },
    [navigate]
  )

  return (
    <>
      <Head title={`${t(name)} (${t("Place Detail")})`} />
      <Box sx={root}>
        <Slide direction="up" in={true} mountOnEnter unmountOnExit>
          <Box sx={wrapper} ref={dialogRef}>
            <Fab variant="rounded" sx={kioskBackButton} onClick={onClose}>
              {backIcon}
            </Fab>
            <AppWeatherWidget sx={weatherWidget} />
            {/**
             * // TODO: Discuss whether to refactor these features into one or keep them separate (TBD).
             */}
            {hasSectionFloors && (
              <Box sx={correlationSelector}>
                <CorrelationSelectorSplide
                  className={NEXTOPIA_CATEGORY_TYPE}
                  syncWithActiveSlide={false}
                  selectedFeatureId={id}
                  correlationOptions={sortedFloorOptions}
                  onChange={handleCorrelationChange}
                />
              </Box>
            )}
            {/**
             * // TODO: Discuss whether to refactor these features into one or keep them separate (TBD).
             */}
            {hasOccupantFloors && (
              <Box sx={correlationOccupantSelector}>
                <CorrelationSelectorSplide
                  syncWithActiveSlide={false}
                  selectedFeatureId={currentFloor}
                  correlationOptions={floorOptions}
                  onChange={handleCorrelationChange}
                />
              </Box>
            )}
            {isNextopiaSection ? (
              <NextopiaPlaceDetailCard
                data={transformedData}
                highlights={transformedPosts}
                canNavigate={canNavigate}
                onHighlightClick={handleHighlightClick}
                onNavigate={onNavigate}
                onViewAllShops={onViewAllShops}
                loaded={dataLoaded}
                mobileUrl={mobileUrl}
              />
            ) : (
              <PlaceDetailCard
                data={transformedData}
                canNavigate={canNavigate}
                onNavigate={onNavigate}
                loaded={dataLoaded}
                mobileUrl={mobileUrl}
              />
            )}
          </Box>
        </Slide>
      </Box>
    </>
  )
}
